import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import View360, { ViewChangeEvent } from '@egjs/react-view360';
import '@egjs/react-view360/css/view360.min.css';

import useGalleryScenes from '../hooks/useGalleryScenes';
import { PanoramaCompass } from './PanoramaCompass';
import { GallerySceneType } from '../CommonTypes';
import { PanoramaSceneSelect } from './PanoramaSceneSelect';
import { PanoramaOverviewPicture } from './PanoramaOverviewPicture';
import { PanoramaHotSpots } from './PanoramaHotSpots';
import { isMobile } from '../states/planner';
import { PanoramaZoomButtons } from './PanoramaZoomButtons';

export const PanoramaViewer = ({
  maxWidth,
  height,
  fullscreen,
}: {
  maxWidth?: string;
  height?: string;
  fullscreen?: boolean;
}) => {
  const viewerRef = useRef<View360 | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  const [galleryScenes] = useGalleryScenes('cube');
  const [selectedScene, setSelectedScene] = useState<GallerySceneType | null>(null);

  const [yaw, setYaw] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [, setZoom] = useState(1);
  const [hFov, setHFov] = useState(75); // degrees
  const [aspect, setAspect] = useState(1); // camera aspect (w/h)

  const currentScene = selectedScene ? selectedScene : galleryScenes?.[0];

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) setContainerEl(node);
  }, []);

  useLayoutEffect(() => {
    if (!containerEl) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (height > 0) setAspect(width / height);
    });
    ro.observe(containerEl);
    return () => ro.disconnect();
  }, [containerEl]);

  useLayoutEffect(() => {
    if (!containerEl || !fullscreen) return;
    Object.defineProperty(document, 'fullscreenElement', {
      configurable: true,
      get: () => containerEl,
    });
    return () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        delete (document as any).fullscreenElement;
      } catch {
        // no-op
      }
    };
  }, [fullscreen, containerEl]);

  const mainWrapperStyle = fullscreen ? {} : { maxWidth: maxWidth ? `clamp(0rem, ${maxWidth}, 90rem)` : '64rem' };
  const contentWrapperStyle = fullscreen
    ? { height: '100dvh', width: '100dvw' }
    : { height: height ? height : '600px', width: '100%' };

  if (!fullscreen && !isMobile()) {
    const v = viewerRef.current;
    v?.control.zoom.disable();
  }

  // Convert zoom -> FOV when view changes
  const handleViewChange = (e: ViewChangeEvent) => {
    setYaw(e.yaw);
    setPitch(e.pitch);
    setZoom(e.zoom);
    const cam = viewerRef.current?.camera;
    if (cam) {
      setHFov(cam.getHorizontalFov(e.zoom)); // <-- this is the key
      setAspect(cam.aspect); // camera knows its own aspect
    }
  };

  return currentScene ? (
    <div id="pintar-planner-panorama-wrapper" className="m-auto" style={mainWrapperStyle}>
      <div
        id="pintar-planner-panorama-content-wrapper"
        className="relative"
        style={contentWrapperStyle}
        ref={containerRef}
      >
        <View360
          ref={viewerRef}
          className="h-full w-full"
          projection={currentScene.projection}
          onViewChange={handleViewChange}
          onProjectionChange={e => e.target.hotspot.refresh()}
          onReady={() => {
            const cam = viewerRef.current?.camera;
            if (cam) {
              setHFov(cam.getHorizontalFov(cam.zoom));
              setAspect(cam.aspect);
            }
          }}
          // keep zoom enabled unless you intentionally disabled it elsewhere
          zoom={{ scale: 1 }}
        >
          <PanoramaCompass yaw={yaw} northOffset={210} />
          <PanoramaSceneSelect currentScene={currentScene} setSelectedScene={setSelectedScene} />
          <PanoramaOverviewPicture
            currentScene={currentScene}
            yaw={yaw}
            pitch={pitch}
            fov={hFov} // pass HORIZONTAL FOV
            aspect={aspect} // (we don't strictly need aspect if we pass horizontal fov)
          />
          <PanoramaHotSpots currentScene={currentScene} setSelectedScene={setSelectedScene} />
          {!fullscreen && !isMobile() && <PanoramaZoomButtons viewerRef={viewerRef} />}
        </View360>
      </div>
    </div>
  ) : null;
};
